import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { DatePipe, registerLocaleData } from '@angular/common';
import localeIt from '@angular/common/locales/it';
import { Ordine, StatoOrdine } from 'src/app/model/ordini/Ordine';
import { OrdiniService } from 'src/app/services/ordini/ordini.service';
import { AppConstants } from 'src/environments/constants';
import { CommonUtils } from 'src/utils/CommonUtils';
import { TableLegendItem } from 'src/app/shared/components/table-legend/table-legend.component';

registerLocaleData(localeIt, 'it-IT');

@Component({
  selector: 'app-dashboard-ordini',
  templateUrl: './dashboard-ordini.component.html',
  styleUrls: ['./dashboard-ordini.component.scss'],
})
export class DashboardOrdiniComponent implements OnInit, OnDestroy {
  private langChangeSub!: Subscription;
  private ordiniCache: Ordine[] = [];
  ordiniDownloadComplete = false;
  ordiniPresenti = false;
  erroreGestitoMex: String = null;
  serverError: boolean = false;
  _isTeamView: boolean = false;
  private datePipe = new DatePipe('it-IT');
  tableConfig;
  legendItems: TableLegendItem[] = [];
  

  emptyImage: string =
    sessionStorage.getItem('theme') === 'dark'
      ? '../../../assets/images/empty-state-dark.svg'
      : '../../../assets/images/empty-state-light.svg';

  private readonly tableHeaderKeys = [
    { text: 'ordini.orderNumber', columnId: 'ordine', width: '20%' },
    { text: 'ordini.supplier', columnId: 'fornitore', width: '20%' },
    { text: 'ordini.positionsProcessed', columnId: 'posizione', width: '20%' },
    { text: 'Dashboard.releaseTab', columnId: 'data', width: '20%' },
    { text: 'ordini.state', columnId: 'stato', width: '25%' },
  ];

  @Input() set isTeamView(value: boolean) {
    if (!CommonUtils.isNotNullOrUndefined(this._isTeamView)) {
      this._isTeamView = value;
    } else if (value !== this._isTeamView) {
      this._isTeamView = value;
      this.caricaOrdini();
    }
  }

  constructor(
    private ordiniServ: OrdiniService,
    private router: Router,
    private translate: TranslateService
  ) {}

  ngOnInit() {
    this.caricaOrdini();
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      if (this.ordiniCache.length > 0) {
        this.buildTableConfig();
      }
    });
  }

  ngOnDestroy() {
    this.langChangeSub?.unsubscribe();
  }

  private buildTableConfig(): void {
    this.legendItems = [
      {
        legendLabel: this.translate.instant('urgent'),
        legendColor: '--charts-red-2',
      },
    ];
    
    const keys = this.tableHeaderKeys.map(h => h.text);
    const translations = this.translate.instant(keys);

    this.tableConfig = {
      tableWidthPercentage: true,
      hasInfoHeader: false,
      tableAriaLabel: this.translate.instant('Dashboard.ordini'),
      tableHeader: {
        tableCellsHeader: this.tableHeaderKeys.map(h => ({
          text: translations[h.text] || h.text,
          columnId: h.columnId,
          columnWidth: h.width,
          textAlign: 'left',
        })),
        hiddenHeader: false,
        secondRightIconSticky: true,
      },
      tableRows: this.ordiniCache.map(ordine =>
        this.mapOrdineToTableRow(ordine)
      ),
      tooltipZindex: 4,
    };
  }

  caricaOrdini() {
    this.ordiniDownloadComplete = false;
    this.ordiniPresenti = false;
    this.erroreGestitoMex = null;
    this.serverError = false;
    this.ordiniServ
      .getOrdini(
        this._isTeamView,
        false,
        false,
        AppConstants.DASHBOARD_FIRST,
        AppConstants.DASHBOARD_ORDINI_LENGHT
      )
      .subscribe(
        res => {
          if (res['errorMex'] != null) {
            this.erroreGestitoMex = res['errorMex'];
            console.log('Get ordini error: ' + res['errorMex']);
          } else {
            let ordini = res['respObj'] || [];
            if (ordini.length > 0) {
              this.ordiniCache = ordini;
              this.buildTableConfig();
              this.ordiniPresenti = true;
            } else {
              this.ordiniCache = [];
              this.ordiniPresenti = false;
            }
          }
          this.ordiniDownloadComplete = true;
        },
        err => {
          this.serverError = true;
          console.error('Error catch', err);
          this.ordiniDownloadComplete = true;
        }
      );
  }

  private mapOrdineToTableRow(ordine: Ordine): any {
    const colorCondition = ordine.urgenza ? '--charts-red-2' : undefined;
    return {
      tableCells: [
        { type: 'text', text: ordine.numeroOrdine || '-' },
        { type: 'text', text: ordine.fornitore?.ragioneSociale || '-' },
        {
          type: 'text',
          text: `${ordine.numeroPosizioniStatoEvaso || 0}/${ordine.numeroPosizioni || 0}`,
        },
        {
          type: 'text',
          text: ordine.dataUltimoRilascio
            ? this.datePipe.transform(
                ordine.dataUltimoRilascio,
                'dd.MM.yyyy'
              ) || '-'
            : '-',
        },
        {
          type: 'text-badge',
          text: ordine.stato
            ? this.formatLabel(this.translate.instant(ordine.stato.toString()))
            : '-',
          badgeStatus: this.getBadgeStatus(ordine.stato).status,
          badgeCustomColor: this.getBadgeStatus(ordine.stato).bkg,
        },
      ],
      rowMarkColor: colorCondition,
      secondRightIcon: 'icon-arrow-right',
      secondRightIconAriaLabel: 'Vai al dettaglio ordine',
    };
  }

  formatLabel(segment) {
    return segment.charAt(0).toUpperCase() + segment.slice(1).toLowerCase();
  }

  getBadgeStatus(element: StatoOrdine): {
    status: any;
    bkg?: string;
  } {
    switch (element) {
      case StatoOrdine.LAVORATO:
        return { status: 'confirm' };
      case StatoOrdine.CHIUSO:
      case StatoOrdine.NON_ASSEGNATO:
        return { status: 'error' };
      case StatoOrdine.NUOVO:
        return { status: 'warning' };
      case StatoOrdine.CANCELLATO:
      case StatoOrdine.REVISIONATO:
      case StatoOrdine.IN_MODIFICA:
        return {
          status: 'custom',
          bkg: 'var(--text-fill-primary)',
        };
      default:
        return { status: 'neutral' };
    }
  }

  onSecondRightIconClick(event: any) {
    let rowIndex: number;
    if (
      typeof event?.detail?.row === 'string' &&
      event.detail.row.startsWith('row-')
    ) {
      rowIndex = Number.parseInt(event.detail.row.replace('row-', ''), 10);
    } else {
      rowIndex = event?.detail?.row;
    }

    const ordine = this.ordiniCache[rowIndex];
    if (ordine?.id) {
      this.router.navigate([AppConstants.ORDINI_ROUTE, ordine.id]);
    }
  }
}
